// Drawing App - Layer Manager

import { Layer } from './types';

export class LayerManager {
  private layers: Layer[] = [];
  private activeLayerId: string = '';
  private canvasWidth: number;
  private canvasHeight: number;
  private onChangeCallback: (() => void) | null = null;

  constructor(width: number, height: number) {
    this.canvasWidth = width;
    this.canvasHeight = height;
    this.addLayer('Background');
  }

  onChange(cb: () => void): void {
    this.onChangeCallback = cb;
  }

  private notifyChange(): void {
    if (this.onChangeCallback) this.onChangeCallback();
  }

  private generateId(): string {
    return 'layer_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  getLayers(): Layer[] {
    return [...this.layers];
  }

  getActiveLayer(): Layer | null {
    return this.layers.find(l => l.id === this.activeLayerId) || null;
  }

  getActiveLayerId(): string {
    return this.activeLayerId;
  }

  setActiveLayer(id: string): void {
    if (this.layers.some(l => l.id === id)) {
      this.activeLayerId = id;
      this.notifyChange();
    }
  }

  getLayerById(id: string): Layer | null {
    return this.layers.find(l => l.id === id) || null;
  }

  addLayer(name?: string): Layer {
    const id = this.generateId();
    const canvas = new OffscreenCanvas(this.canvasWidth, this.canvasHeight);
    const layer: Layer = {
      id,
      name: name || `Layer ${this.layers.length + 1}`,
      canvas,
      opacity: 1,
      blendMode: 'source-over',
      visible: true,
      locked: false
    };
    this.layers.push(layer);
    this.activeLayerId = id;
    this.notifyChange();
    return layer;
  }

  removeLayer(id: string): boolean {
    if (this.layers.length <= 1) return false;
    const index = this.layers.findIndex(l => l.id === id);
    if (index === -1) return false;

    this.layers.splice(index, 1);
    if (this.activeLayerId === id) {
      this.activeLayerId = this.layers[Math.min(index, this.layers.length - 1)].id;
    }
    this.notifyChange();
    return true;
  }

  duplicateLayer(id: string): Layer | null {
    const source = this.layers.find(l => l.id === id);
    if (!source) return null;

    const newCanvas = new OffscreenCanvas(this.canvasWidth, this.canvasHeight);
    const ctx = newCanvas.getContext('2d')!;
    ctx.drawImage(source.canvas, 0, 0);

    const newLayer: Layer = {
      id: this.generateId(),
      name: source.name + ' (copy)',
      canvas: newCanvas,
      opacity: source.opacity,
      blendMode: source.blendMode,
      visible: true,
      locked: false
    };

    const index = this.layers.findIndex(l => l.id === id);
    this.layers.splice(index + 1, 0, newLayer);
    this.activeLayerId = newLayer.id;
    this.notifyChange();
    return newLayer;
  }

  moveLayer(id: string, direction: 'up' | 'down'): boolean {
    const index = this.layers.findIndex(l => l.id === id);
    if (index === -1) return false;

    const newIndex = direction === 'up' ? index + 1 : index - 1;
    if (newIndex < 0 || newIndex >= this.layers.length) return false;

    const temp = this.layers[index];
    this.layers[index] = this.layers[newIndex];
    this.layers[newIndex] = temp;
    this.notifyChange();
    return true;
  }

  setLayerOpacity(id: string, opacity: number): void {
    const layer = this.layers.find(l => l.id === id);
    if (layer) {
      layer.opacity = Math.max(0, Math.min(1, opacity));
      this.notifyChange();
    }
  }

  setLayerBlendMode(id: string, mode: GlobalCompositeOperation): void {
    const layer = this.layers.find(l => l.id === id);
    if (layer) {
      layer.blendMode = mode;
      this.notifyChange();
    }
  }

  setLayerVisibility(id: string, visible: boolean): void {
    const layer = this.layers.find(l => l.id === id);
    if (layer) {
      layer.visible = visible;
      this.notifyChange();
    }
  }

  setLayerLocked(id: string, locked: boolean): void {
    const layer = this.layers.find(l => l.id === id);
    if (layer) {
      layer.locked = locked;
      this.notifyChange();
    }
  }

  renameLayer(id: string, name: string): void {
    const layer = this.layers.find(l => l.id === id);
    if (layer) {
      layer.name = name;
      this.notifyChange();
    }
  }

  clearLayer(id: string): void {
    const layer = this.layers.find(l => l.id === id);
    if (layer) {
      const ctx = layer.canvas.getContext('2d')!;
      ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
      this.notifyChange();
    }
  }

  getLayerSnapshot(id: string): ImageData | null {
    const layer = this.layers.find(l => l.id === id);
    if (!layer) return null;
    const ctx = layer.canvas.getContext('2d')!;
    return ctx.getImageData(0, 0, this.canvasWidth, this.canvasHeight);
  }

  restoreLayerSnapshot(id: string, imageData: ImageData): void {
    const layer = this.layers.find(l => l.id === id);
    if (!layer) return;
    const ctx = layer.canvas.getContext('2d')!;
    ctx.putImageData(imageData, 0, 0);
    this.notifyChange();
  }

  mergeDown(id: string): boolean {
    const index = this.layers.findIndex(l => l.id === id);
    if (index <= 0) return false;

    const upper = this.layers[index];
    const lower = this.layers[index - 1];
    const ctx = lower.canvas.getContext('2d')!;
    ctx.globalAlpha = upper.opacity;
    ctx.globalCompositeOperation = upper.blendMode;
    ctx.drawImage(upper.canvas, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';

    this.layers.splice(index, 1);
    this.activeLayerId = lower.id;
    this.notifyChange();
    return true;
  }
}
